import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { loginUser, getCurrentUser } from '../services/api';
import { AuthState, User } from '../types';

interface AuthContextType {
    authState: AuthState;
    user: User | null;
    login: (email: string, password: string) => Promise<any>;
    logout: () => void;
    updateUser: (user: User) => void;
}

const defaultAuthState: AuthState = {
    user: null,
    token: null,
    isAuthenticated: false,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [authState, setAuthState] = useState<AuthState>(() => {
        // Load auth state from localStorage
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (token && user) {
            return {
                user: JSON.parse(user) as User,
                token,
                isAuthenticated: true,
                timezone,
            };
        }

        return defaultAuthState;
    });

    useEffect(() => {
        // Save auth state to localStorage when it changes
        if (authState.token && authState.user) {
            localStorage.setItem('token', authState.token);
            localStorage.setItem('user', JSON.stringify(authState.user));
        } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        }
    }, [authState]);

    const logout = useCallback(() => {
        setAuthState(defaultAuthState);
    }, []);

    // Fetch current user data on app load to get latest role/permissions
    useEffect(() => {
        const syncUserData = async () => {
            // Only try to sync if we have a valid token
            if (authState.isAuthenticated && authState.token && authState.user) {
                try {
                    const currentUserData = await getCurrentUser();
                    if (currentUserData) {
                        setAuthState(prev => ({
                            ...prev,
                            user: currentUserData
                        }));
                    }
                } catch (error: any) {
                    // Logout on authentication errors (401) or browser access restriction (403 with requiresElectronApp)
                    console.error('Failed to sync user data:', error);
                    if (error.response?.status === 401 || 
                        (error.response?.status === 403 && error.response?.data?.requiresElectronApp)) {
                        logout();
                    }
                    // For other errors, just log them but don't logout
                }
            }
        };

        syncUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount - intentionally excluding authState dependencies

    // Listen for logout events from API interceptor
    useEffect(() => {
        const handleLogoutEvent = () => {
            logout();
        };

        window.addEventListener('auth:logout', handleLogoutEvent);
        return () => {
            window.removeEventListener('auth:logout', handleLogoutEvent);
        };
    }, [logout]);

    const login = async (email: string, password: string) => {
        try {
            const data = await loginUser(email, password);

            if (!data.token) {
                throw new Error('No token received from server');
            }

            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

            // Save to localStorage immediately
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));

            setAuthState({
                user: data.user,
                token: data.token,
                isAuthenticated: true,
                timezone,
            });

            return data; // Return the login data including user info
        } catch (error) {
            console.error('Login error');
            throw error;
        }
    };

    const updateUser = (user: User) => {
        setAuthState(prev => ({
            ...prev,
            user
        }));
    };

    const value = {
        authState,
        user: authState.user,
        login,
        logout,
        updateUser,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}; 